import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {Container} from "../../components/Container"
import {Chart, ArcElement, Tooltip, Legend, Colors} from "chart.js/auto"
import {C, calculateWeekDay, sum} from "./utils/calculate"
import {useDispatch, useSelector} from "react-redux"
import timeConverter from "./utils/time"
import useStaticData from "./data/static"
import {PhaseField} from "./components/PhaseField"
import {DoughnutChart} from "./components/DoughnutChart"
import {BarChart} from "./components/BarChart"
import {AmountField} from "./components/AmountField"
import {PieChart} from "./components/PieChart"
import {useRPC} from "../../hooks/rpc"
import {EnergyModel} from "../../store/control/state"
import {barDatas, doughnutDatas, pieDatas} from "./settings/options"
import {backButtonToggle} from "../../store/ui/actions"
import {PhaseModel} from "./types/types"
import {useLocalization} from "../../hooks/localization"
import "./index.scss"

Chart.register(ArcElement, Tooltip, Legend, Colors)
Chart.defaults.color = "#fff"
Chart.defaults.borderColor = "rgb(136, 136, 136)"
Chart.defaults.font.size = 8

//type EnergyStatusType = Omit<EnergyMonitor, "timestamp"> & {timestamp: number}
const date = new Date()
export const EnergyScreen = memo(function EnergyScreen() {
    const [rpc] = useRPC()
    const {t} = useLocalization()
    const energyUpdated = useSelector(state => state.control.energyUpdated)
    const controls = useSelector(state => state.control.controlList)
    const controlList = controls.filter(control => control.type_id === "200")
    const energyControlList = useSelector(state => state.control.energyState)

    const [defaultColor, setDefaultColor] = useState(null)
    const [barChartLabel, setBarChartLabel] = useState("hour")
    const [energyHourly, setEnergyHourly] = useState<EnergyModel[]>([])
    const [energyDaily, setEnergyDaily] = useState<EnergyModel[]>([])
    const [energyMonthly, setEnergyMonthly] = useState<EnergyModel[]>([])
    const [energyHourlyStatistic, setEnergyHourlyStatistic] = useState<EnergyModel[]>([])
    const [energyDailyStatistic, setEnergyDailyStatistic] = useState<EnergyModel[]>([])
    const [energyMonthlyStatistic, setEnergyMonthlyStatistic] = useState<EnergyModel[]>([])
    const maxValue = Number(energyControlList.max_point)
    const dispatch = useDispatch()
    const {hours, days, months} = useStaticData()
    dispatch(backButtonToggle({is_open: true, to: "/"}))

    const sendDate = `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}`;

    /*const [state, setState] = useState<EnergyStatusType[]>([])
    useEffect(() => {
        setState(p => [...p, {control_id: energyUpdated?.control_id, energy_value: energyUpdated?.energy_value, timestamp: Date.now()}])
    }, [energyUpdated])*/

    const Phases: PhaseModel[] = useMemo(() => controlList, [controlList])
    const [selectedPhase, setSelectedPhase] = useState({
        id: Phases[0]?.id || undefined,
        selected: true
    })
    const realtimeEnergy = useSelector(
        state => state.control.controlList.find(r => r?.id === selectedPhase?.id)?.parameters["realtime_energy"]
    )
    
    const energyUpdatedCheck = useMemo(
        () => (energyUpdated?.energy_value === null ? Number(realtimeEnergy) : Number(energyUpdated?.energy_value)),
        [energyUpdated, realtimeEnergy]
    )
    const [valueInput, setValueInput] = useState<any>(energyUpdatedCheck)
    const [defaultValue, setDefaultValue] = useState(0)

    /*const newFilterValue = state.filter(state => state?.control_id !== null).sort((a, b) => a?.timestamp > b?.timestamp ? -1 : 1).find(value => value?.control_id === selectedPhase?.id)*/

    const onClickRequestEnergyMonitor = useCallback(() => {
        rpc.GetEnergyMonitorHourly({control_id: selectedPhase?.id, date: sendDate}).then(data =>
            setEnergyHourly(data || [])
        )
        rpc.GetEnergyMonitorDaily({control_id: selectedPhase?.id, date: sendDate}).then(data =>
            setEnergyDaily(data || [])
        )
        rpc.GetEnergyMonitorMonthly({control_id: selectedPhase?.id, date: sendDate}).then(data =>
            setEnergyMonthly(data || [])
        )
    }, [rpc, selectedPhase, sendDate])

    const onClickRequestEnergyStatistics = useCallback(() => {
        rpc.EnergyHourlyStatistics(sendDate).then(data => setEnergyHourlyStatistic(data || []))
        rpc.EnergyDailyStatistics(sendDate).then(data => setEnergyDailyStatistic(data || []))
        rpc.EnergyMonthlyStatistics(sendDate).then(data => setEnergyMonthlyStatistic(data || []))
    }, [rpc, sendDate])

    const returnedFilterMonths: number[] = useMemo(() => {
        const data = []
        const findValue = (month: number) =>
            energyMonthly.filter(value => new Date(value?.month).getMonth() + 1 === month).find(total => total.total)
                ?.total
        for (let j = 1; j <= 12; j++) {
            data.push({
                month: j,
                total: findValue(j) || 0
            })
        }
        return data.map(value => value?.total)
    }, [energyMonthly])

    const returnedFilterDays: number[] = useMemo(() => {
        /*const monthDayCount = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
        const getAllDate = (day: number) => new Date(date.getFullYear(), date.getMonth(), day).getDay()
        const data = []
        const findValue = (day: number) =>
            energyDaily.filter(value => new Date(value?.date).getDate() === day).find(total => total.total)?.total
        const pastDays = monthDayCount - date.getDate()
        const followingDays = monthDayCount - pastDays
        for (let j = 1; j <= followingDays; j++) {
            data.push({
                week: getAllDate(j),
                day: j,
                total: findValue(j) || 0
            })
        }*/

        return energyDaily.map(value => value.total).reverse()
    }, [energyDaily])

    const returnedFilterHours: number[] = useMemo(() => {
        const today = date.getDate()
        const findValue = (hour: number) =>
            energyHourly
                .filter(
                    value =>
                        Number(timeConverter(value?.time).day) === today &&
                        Number(timeConverter(value?.time).hour) === hour
                )
                .map(value => value.value)
                .find(total => total)
        const data = []
        for (let j = 0; j <= 23; j++) {
            data.push({
                value: findValue(j) || 0,
                time: j
            })
        }
        return data.map(data => data?.value)
    }, [energyHourly])

    const barChartLabels: string[] = useMemo(() => {
        if (barChartLabel === "hour") {
            return hours
        } else if (barChartLabel === "day") {
            return days
        } else if (barChartLabel === "month") {
            return months
        }
    }, [barChartLabel, hours, days, months])

    const barChartData: number[] = useMemo(() => {
        if (barChartLabel === "hour") {
            return returnedFilterHours
        } else if (barChartLabel === "day") {
            return returnedFilterDays
        } else if (barChartLabel === "month") {
            return returnedFilterMonths
        }
    }, [barChartLabel, returnedFilterHours, returnedFilterMonths, returnedFilterDays])

    const selectedAmount = useCallback((list: EnergyModel[], id: string) => {
        return list.find(r => (r?.control_id === id ? r?.price : 0))?.price
    }, [])

    const [amountValue, setAmountValue] = useState(selectedAmount(energyHourlyStatistic, selectedPhase?.id))

    const controlListFilter = useCallback((statistic: EnergyModel[], id: string) => {
        const returnData = []
        for (const handle of statistic) {
            returnData.push({
                control_id: handle.control_id,
                total: handle.total
            })
        }
        const filteredData = returnData
            .filter(control => control.control_id === id && control)
            .map(total => total.total)
        return filteredData
    }, [])

    const queryStatisticType = useMemo(() => {
        switch (barChartLabel) {
            case "hour":
                return energyHourlyStatistic
            case "day":
                return energyDailyStatistic
            case "month":
                return energyMonthlyStatistic
        }
    }, [barChartLabel, energyHourlyStatistic, energyDailyStatistic, energyMonthlyStatistic])

    const controlListValues = useMemo(() => {
        const data = []
        for (const value of controlList) {
            data.push({
                id: value?.id,
                name: value?.name,
                value: controlListFilter(queryStatisticType, value?.id) || 0
            })
        }
        return data.filter(filteredControl => filteredControl.value).sort((a, b) => (a.value > b.value ? -1 : 1))
    }, [controlList, controlListFilter, queryStatisticType])

    const controlListOther: number[] = useMemo(() => {
        const data = []
        for (const values of controlListValues) {
            data.push({
                value: values.value
            })
        }
        return data
            .map(controlValue => controlValue.value)
            .sort((a, b) => (a.value > b.value ? -1 : 1))
            .splice(4)
    }, [controlListValues])

    const controlListControl: string[] = useMemo(() => {
        const controlListCondition = controlListValues
            .map(controlName => controlName.name)
            .sort((a, b) => (a.name > b.name ? -1 : 1))
            .slice(0, 4)
        return controlListValues.length >= 5
            ? [...controlListCondition, t("media_device_type.Other")]
            : [...controlListCondition]
    }, [controlListValues, t])

    const controlListValueControl: number[] = useMemo(() => {
        const controlListCondition = controlListValues.map(controlValue => controlValue.value).slice(0, 4)
        return controlListValues.length >= 5
            ? [...controlListCondition, controlListOther.reduce(sum, 0)]
            : [...controlListCondition]
    }, [controlListOther, controlListValues])

    /*const barChartColors = useMemo(() => {
        const day = date.getDay()
        let activeColor = "rgb(255, 187, 0)",
            defaultColor = "rgb(7, 255, 40)"
        const colorRepeat = (multiple: number): string[] => {
            const data: string[] = []
            for (let i = 0; i < multiple; i++) {
                data.push(defaultColor)
            }
            return data
        }
        if (barChartLabel === "hour") {
        } else if (barChartLabel === "day") {
            switch (day) {
                case 0:
                    return [...colorRepeat(6), activeColor]
                case 1:
                    return [activeColor, ...colorRepeat(6)]
                case 2:
                    return [defaultColor, activeColor, ...colorRepeat(5)]
                case 3:
                    return [...colorRepeat(2), activeColor, ...colorRepeat(4)]
                case 4:
                    return [...colorRepeat(3), activeColor, ...colorRepeat(3)]
                case 5:
                    return [...colorRepeat(4), activeColor, ...colorRepeat(2)]
                case 6:
                    return [...colorRepeat(5), activeColor, defaultColor]
            }
        } else if (barChartLabel === "month") {
        }
    }, [barChartLabel])*/

    const barChartDatas = barDatas(barChartLabels, barChartData, ["rgb(7, 255, 40)"])
    const doughnutChartData = doughnutDatas(valueInput, defaultValue, defaultColor)
    const pieChartData = pieDatas(controlListControl, controlListValueControl, controlList)

    const onClickAmountStatus = useCallback(
        (action: number) => {
            let newChartLabel = barChartLabel
            let newChartAmount = amountValue
            if (action === 1) {
                newChartLabel = "hour"
            } else if (action === 2) {
                newChartLabel = "day"
            } else if (action === 3) {
                newChartLabel = "month"
            }
            setBarChartLabel(newChartLabel)
            if (newChartLabel === "hour") {
                newChartAmount = selectedAmount(energyHourlyStatistic, selectedPhase?.id)
            } else if (newChartLabel === "day") {
                newChartAmount = selectedAmount(energyDailyStatistic, selectedPhase?.id)
            } else if (newChartLabel === "month") {
                newChartAmount = selectedAmount(energyMonthlyStatistic, selectedPhase?.id)
            }
            setAmountValue(newChartAmount)
            onClickRequestEnergyStatistics()
            onClickRequestEnergyMonitor()
        },
        [
            barChartLabel,
            amountValue,
            selectedAmount,
            onClickRequestEnergyStatistics,
            onClickRequestEnergyMonitor,
            energyHourlyStatistic,
            energyDailyStatistic,
            energyMonthlyStatistic,
            selectedPhase?.id
        ]
    )

    useEffect(
        () => setAmountValue(selectedAmount(queryStatisticType, selectedPhase?.id)),
        [selectedAmount, queryStatisticType, selectedPhase?.id]
    )

    const onClickPhaseChanging = useCallback(
        (id: string) => {
            Phases.find(
                phase =>
                    phase.id === id &&
                    setSelectedPhase({
                        id: id,
                        selected: true
                    })
            )
            onClickRequestEnergyStatistics()
            onClickRequestEnergyMonitor()
        },
        [Phases, onClickRequestEnergyStatistics, onClickRequestEnergyMonitor]
    )
    /*const onClickAllPhase = useCallback(() => {
        setSelectedPhase({
            id: undefined,
            selected: false
        })
    }, [])*/

    useEffect(() => onClickRequestEnergyMonitor(), [onClickRequestEnergyMonitor])
    useEffect(() => onClickRequestEnergyStatistics(), [onClickRequestEnergyStatistics])

    useEffect(() => {
        let dataGraphic = valueInput
        let defaultdataGraphic = defaultValue
        let dataColor = defaultColor
        if (dataGraphic <= maxValue && dataGraphic >= 0) {
            defaultdataGraphic = maxValue - dataGraphic
            if (dataGraphic >= 0 && dataGraphic <= C(maxValue, 7)) {
                dataColor = "rgba(81, 243, 51, 1)"
            } else if (dataGraphic >= C(maxValue, 7) + 1 && dataGraphic <= C(maxValue, 6)) {
                dataColor = "rgba(197, 245, 61, 1)"
            } else if (dataGraphic >= C(maxValue, 6) + 1 && dataGraphic <= C(maxValue, 5)) {
                dataColor = "rgba(252, 196, 38, 1)"
            } else if (dataGraphic >= C(maxValue, 5) + 1 && dataGraphic <= C(maxValue, 4)) {
                dataColor = "rgba(252, 143, 38, 1)"
            } else if (dataGraphic >= C(maxValue, 4) + 1 && dataGraphic <= C(maxValue, 3)) {
                dataColor = "rgba(246, 97, 39, 1)"
            } else if (dataGraphic >= C(maxValue, 3) + 1 && dataGraphic <= C(maxValue, 2)) {
                dataColor = "rgba(236, 65, 18, 1)"
            } else if (dataGraphic >= C(maxValue, 2) + 1 && dataGraphic <= maxValue) {
                dataColor = "rgba(255, 0, 0, 1)"
            }
        } else {
            dataGraphic = maxValue
            defaultdataGraphic = 0
            dataColor = "rgba(255, 0, 0, 1)"
        }
        setDefaultValue(defaultdataGraphic)
        setDefaultColor(dataColor)
        setValueInput(energyUpdatedCheck)
    }, [defaultColor, defaultValue, valueInput, maxValue, energyUpdatedCheck, selectedPhase])


    return (
        <Container>
            <div className="page-wrapper">
                <div className="energy-wrapper">
                    <div className="energy-page-container">
                        <PhaseField
                            selectedPhase={selectedPhase}
                            //onClickAllPhase={onClickAllPhase}
                            onClickPhaseChanging={onClickPhaseChanging}
                        />
                    </div>
                    <div className="energy-page-container">
                        <DoughnutChart data={doughnutChartData} value={valueInput.toFixed(2)} page={1} />
                        <BarChart
                            data={barChartDatas}
                            label={barChartLabel}
                            amountStatus={onClickAmountStatus}
                            page={2}
                        />
                    </div>
                    <div className="energy-page-container">
                        <AmountField
                            label={barChartLabel}
                            amount={amountValue}
                            amountStatus={onClickAmountStatus}
                            page={3}
                        />
                        <PieChart
                            data={pieChartData}
                            page={4}
                            label={barChartLabel}
                            amountStatus={onClickAmountStatus}
                        />
                    </div>
                </div>
            </div>
        </Container>
    )
})
